import React, { useMemo, useState } from "react";
import _ from "lodash";
import { Button, Modal } from "../../common";
import CircularProgressRing from "./CircularProgressRing";

export type ProfileTaskKey =
  | "photo"
  | "video"
  | "resume"
  | "basic"
  | "passion"
  | "values"
  | "education"
  | "experience"
  | "projects"
  | "skills";

export interface ProfileTask {
  key: ProfileTaskKey;
  title: string;
  description: string;
  percent: number;
  status: "Not Started" | "Incomplete" | "Completed";
  actionLabel: string;
}

interface ProfileProgressCardProps {
  tokenData: any;
  hasPrimaryVideo?: boolean;
  onCompleteTask: (key: ProfileTaskKey) => void;
}

const hasValue = (value: unknown) => {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.replace(/<[^>]*>/g, "").trim().length > 0;
  return Boolean(value);
};

const recordPercent = (
  records: any[] | undefined,
  isComplete: (row: any) => boolean
) => {
  if (!records?.length) return 0;
  const completeCount = records.filter(isComplete).length;
  if (completeCount === records.length) return 100;
  return Math.max(40, Math.round((completeCount / records.length) * 100));
};

const statusForPercent = (percent: number): ProfileTask["status"] => {
  if (percent >= 100) return "Completed";
  if (percent <= 0) return "Not Started";
  return "Incomplete";
};

const fieldsPercent = (flags: boolean[]) => {
  const completeCount = flags.filter(Boolean).length;
  if (!flags.length || completeCount === 0) return 0;
  if (completeCount === flags.length) return 100;
  return Math.round((completeCount / flags.length) * 100);
};

export const buildProfileTasks = (
  tokenData: any,
  hasPrimaryVideo = false
): ProfileTask[] => {
  const profile = tokenData?.profile || {};
  const education = tokenData?.education || [];
  const experiences = tokenData?.experiences || [];
  const projects = tokenData?.projects || [];
  const skills = tokenData?.skills || [];
  const resumes = tokenData?.resumes || [];

  const photoDone = hasValue(tokenData?.profile_image);
  const videoDone = hasPrimaryVideo || hasValue(profile?.profile_video);
  const resumeDone = resumes.length > 0 || hasValue(profile?.resume_file);
  const basicPercent = fieldsPercent([
    Boolean(profile?.city_id || profile?.location?.id),
    hasValue(profile?.work_status),
    hasValue(profile?.job_title) ||
    hasValue(profile?.job_title_custom) ||
    hasValue(profile?.jobTitle?.title),
  ]);
  const valuesPercent = hasValue(profile?.personal_values) ? 100 : 0;
  const passionPercent = fieldsPercent([
    hasValue(profile?.early_career),
    hasValue(profile?.mid_career),
    hasValue(profile?.recent_life),
  ]);

  const educationPercent = recordPercent(
    education,
    (row) => hasValue(row?.institute_name) && hasValue(row?.degree_title)
  );
  const experiencePercent = recordPercent(
    experiences,
    (row) =>
      hasValue(row?.company_name) &&
      (hasValue(row?.jobTitle?.label) || hasValue(row?.job_title_custom))
  );
  const projectsPercent = recordPercent(projects, (row) => hasValue(row?.title));
  const skillsPercent = skills.length > 0 ? 100 : 0;

  const tasks: Array<ProfileTask & { done: boolean }> = [
    {
      key: "video",
      title: "Introduction Video",
      description:
        "Add a short video to make your profile more engaging and stand out to employers.",
      percent: videoDone ? 100 : 0,
      status: statusForPercent(videoDone ? 100 : 0),
      actionLabel: videoDone ? "Update" : "Upload",
      done: videoDone,
    },
    {
      key: "photo",
      title: "Profile Photo",
      description: "Add a clear profile photo so recruiters can recognize you.",
      percent: photoDone ? 100 : 0,
      status: statusForPercent(photoDone ? 100 : 0),
      actionLabel: photoDone ? "Update" : "Upload",
      done: photoDone,
    },
    {
      key: "education",
      title: "Education",
      description: "Add your education details to complete this section.",
      percent: educationPercent,
      status: statusForPercent(educationPercent),
      actionLabel: educationPercent === 0 ? "Add" : "Update",
      done: educationPercent === 100,
    },
    {
      key: "experience",
      title: "Work Experience",
      description: "Add your past work experience to strengthen your profile.",
      percent: experiencePercent,
      status: statusForPercent(experiencePercent),
      actionLabel: experiencePercent === 0 ? "Add" : "Update",
      done: experiencePercent === 100,
    },
    {
      key: "resume",
      title: "Resume",
      description: "Upload a resume so you can apply to jobs faster.",
      percent: resumeDone ? 100 : 0,
      status: statusForPercent(resumeDone ? 100 : 0),
      actionLabel: resumeDone ? "Update" : "Upload",
      done: resumeDone,
    },
    {
      key: "skills",
      title: "Skills",
      description: "Add the skills that best represent your experience.",
      percent: skillsPercent,
      status: statusForPercent(skillsPercent),
      actionLabel: skillsPercent === 100 ? "Update" : "Add",
      done: skillsPercent === 100,
    },
    {
      key: "projects",
      title: "Projects",
      description: "Share projects that highlight what you can do.",
      percent: projectsPercent,
      status: statusForPercent(projectsPercent),
      actionLabel: projectsPercent === 0 ? "Add" : "Update",
      done: projectsPercent === 100,
    },
    {
      key: "basic",
      title: "Profile Details",
      description: "Add your location, work status, and job title.",
      percent: basicPercent,
      status: statusForPercent(basicPercent),
      actionLabel: basicPercent === 0 ? "Add" : "Update",
      done: basicPercent === 100,
    },
    {
      key: "values",
      title: "Personal Values",
      description: "Share the values that guide how you work and what you care about.",
      percent: valuesPercent,
      status: statusForPercent(valuesPercent),
      actionLabel: valuesPercent === 0 ? "Add" : "Update",
      done: valuesPercent === 100,
    },
    {
      key: "passion",
      title: "Professional Passion",
      description:
        "Tell your career story — earlier years, mid journey, and recent career.",
      percent: passionPercent,
      status: statusForPercent(passionPercent),
      actionLabel: passionPercent === 0 ? "Add" : "Update",
      done: passionPercent === 100,
    },
  ];

  return tasks.map(({ done, ...task }) => task);
};

const ProfileProgressCard: React.FC<ProfileProgressCardProps> = ({
  tokenData,
  hasPrimaryVideo = false,
  onCompleteTask,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const overallProgress = Math.min(
    100,
    Math.max(0, Number(_.get(tokenData, "profile.profile_progress", 0)) || 0)
  );
  const profileTasks = useMemo(
    () => buildProfileTasks(tokenData, hasPrimaryVideo),
    [tokenData, hasPrimaryVideo]
  );
  const isComplete =
    overallProgress >= 100 || profileTasks.every((task) => task.percent >= 100);

  const handleTaskClick = (key: ProfileTaskKey) => {
    setModalOpen(false);
    window.setTimeout(() => onCompleteTask(key), 180);
  };

  return (
    <>
      <div className="profile-progress-card">
        <div className="profile-progress-card__copy">
          <b>Complete Your Profile</b>
          <p>
            {isComplete
              ? "Your profile is complete. Keep it updated so recruiters always see your latest work."
              : "Increase your chances of getting noticed by recruiters by completing your profile."}
          </p>
          {!isComplete && (
            <button
              type="button"
              className="profile-progress-card__cta"
              onClick={() => setModalOpen(true)}
            >
              Complete now
              <span aria-hidden="true">→</span>
            </button>
          )}
        </div>
        <CircularProgressRing value={overallProgress} size={96} strokeWidth={6} onClick={() => setModalOpen(true)} />
      </div>

      <Modal
        className="complete-profile-modal"
        visible={modalOpen}
        size="medium"
        title="Complete Your Profile"
        closeButton
        closeOnBackDrop
        onClose={() => setModalOpen(false)}
      >
        <div className="complete-profile-modal__list">
          {profileTasks.map((task) => (
            <div key={task.key} className="complete-profile-modal__item">
              <div className="complete-profile-modal__item-copy">
                <div className="complete-profile-modal__item-title">
                  <h4>{task.title}</h4>

                </div>
                <p>{task.description}</p>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  className="complete-profile-modal__action"
                  onClick={() => handleTaskClick(task.key)}
                >
                  {task.actionLabel}
                  <span aria-hidden="true" className="pl-1">→</span>
                </Button>
              </div>

              <span
                className={`complete-profile-modal__status ${task.status === "Not Started"
                  ? "is-new"
                  : task.status === "Completed"
                    ? "is-complete"
                    : "is-incomplete"
                  }`}
              >
                {task.status}
              </span>
            </div>
          ))}
        </div>
        <div className="complete-profile-modal__footer">
          <Button color="primary" onClick={() => setModalOpen(false)}>
            Close
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ProfileProgressCard;
